/**
 * track.js — 产品行为埋点
 *
 * 同时写入：
 *   1. Supabase usage_events 表（内部分析）
 *   2. Plausible 自定义事件（修复 SPA 下 visit duration = 0 的问题）
 *
 * Plausible 用法说明：
 *   PrimaryLens 是单页应用，用户在页内交互不触发路由变化，
 *   Plausible 只能收到一个 pageview 事件，导致无法计算 visit duration。
 *   每次 track() 调用同步向 Plausible 发送自定义事件，
 *   Plausible 即可用「首个 pageview → 最后一个事件」计算真实停留时长。
 *
 * 用法：
 *   import { track } from '../utils/track';
 *   track('project_analyzed', { project_name: 'Monad', composite: 82, from_cache: false });
 */
import { supabase } from '../lib/supabase';

const PROXY = process.env.REACT_APP_PROXY_URL || 'https://primary-lens-production.up.railway.app';

// 每次页面加载生成一个会话 ID，用于区分独立访客
export const SESSION_ID = Math.random().toString(36).slice(2, 10);

// session_start 时调一次：服务端从 IP+UA 提取 country/browser/os 写入
// 之后的事件继续直写 supabase，避免每次都过 proxy
async function enrichSessionOnServer(event, props, userId) {
  try {
    await fetch(`${PROXY}/api/track-enriched`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, properties: props, session_id: SESSION_ID, user_id: userId || null }),
      // keepalive 让 session_end 在 unload 时也能成功发送
      keepalive: true,
    }).catch(() => {});
  } catch { /* 静默 */ }
}

// 访客元数据（设备、来源、时区）— 首次访问时采集一次
function getVisitorMeta() {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const referrerRaw = typeof document !== 'undefined' ? document.referrer : '';
  let referrer = 'direct';
  if (referrerRaw) {
    try {
      const host = new URL(referrerRaw).hostname.replace('www.', '');
      if (host.includes('twitter') || host.includes('t.co') || host.includes('x.com')) referrer = 'Twitter/X';
      else if (host.includes('google'))   referrer = 'Google';
      else if (host.includes('telegram')) referrer = 'Telegram';
      else if (host.includes('linkedin')) referrer = 'LinkedIn';
      else if (host.includes('discord'))  referrer = 'Discord';
      else if (host.includes('github'))   referrer = 'GitHub';
      else                                referrer = host;
    } catch { referrer = 'other'; }
  }
  const tz = (typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : '') || '';
  const region = tz.includes('Asia')      ? 'Asia'
               : tz.includes('Europe')    ? 'Europe'
               : tz.includes('America')   ? 'Americas'
               : tz.includes('Africa')    ? 'Africa'
               : tz.includes('Australia') || tz.includes('Pacific') ? 'Oceania'
               : 'Other';
  return {
    referrer,
    device: w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop',
    tz,
    region,
    lang: typeof navigator !== 'undefined' ? (navigator.language?.slice(0, 5) || '') : '',
  };
}

// 对 session_start / session_end / page_view 事件自动注入访客 meta
const VISITOR_META_EVENTS = new Set(['session_start', 'session_end', 'page_view']);

/**
 * @param {string} event       - 事件名，见 004_usage_events.sql 注释
 * @param {object} properties  - 事件附带属性（任意 JSON）
 */
export async function track(event, properties = {}) {
  const props = VISITOR_META_EVENTS.has(event)
    ? { ...getVisitorMeta(), ...properties }
    : properties;

  // 1. 向 Plausible 发送自定义事件（同步，不依赖 supabase）
  try {
    if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
      window.plausible(event, { props });
    }
  } catch {
    // 静默
  }

  // 2. 写入 Supabase
  if (!supabase) return;
  try {
    const { data: { user } } = await supabase.auth.getUser().catch(() => ({ data: {} }));
    await supabase.from('usage_events').insert({
      event,
      properties: props,
      user_id: user?.id || null,
      session_id: SESSION_ID,
    });
    // 3. session_start 时额外触发服务端 enrich（IP→country + UA→browser/os）
    // 一个 session 一次，副本事件名 = 'session_enriched'，supabase 写双份不影响 KPI
    // （aggregator 按 session_id 合并 properties）
    if (event === 'session_start') {
      enrichSessionOnServer('session_enriched', props, user?.id);
    }
  } catch {
    // 埋点失败静默，绝不影响用户操作
  }
}
